<template>
    <section class="badges-panel" aria-label="Badges du chauffeur">
        <div class="badges-heading">
            <div>
                <h3>Badges associés</h3>
                <p>Associez ou retirez les badges de ce chauffeur.</p>
            </div>
            <span class="badge-count">{{ badges.length }}</span>
        </div>

        <p v-if="loading" class="badge-hint" role="status">Chargement des badges…</p>
        <p v-else-if="loadError" class="badge-error" role="alert">{{ loadError }} <button type="button"
                @click="loadBadges">Réessayer</button></p>
        <template v-else>
            <p v-if="!badges.length" class="badge-empty">Aucun badge associé à ce chauffeur.</p>
            <ul v-else class="badge-list">
                <li v-for="badge in badges" :key="badge.id" class="badge-item">
                    <div class="badge-info">
                        <strong>{{ badge.ref || 'Sans référence' }}</strong>
                        <span>Slot balise : {{ badge.slot != null ? badge.slot : '—' }}</span>
                    </div>
                    <button type="button" class="remove-button" :disabled="busy"
                        :aria-label="'Dissocier le badge ' + (badge.ref || badge.id)"
                        @click="removeBadge(badge)">Retirer</button>
                </li>
            </ul>
            <div class="badge-association">
                <label for="available-driver-badge">Associer un badge disponible</label>
                <div class="association-controls">
                    <select id="available-driver-badge" v-model="selectedBadgeId"
                        :disabled="busy || loadingAvailable || !availableBadges.length">
                        <option value="">
                            {{ loadingAvailable ? 'Chargement…' :
                                (availableBadges.length ? 'Choisir un badge' : 'Aucun badge disponible')
                            }}
                        </option>
                        <option v-for="badge in availableBadges" :key="badge.id" :value="String(badge.id)">{{ badge.ref
                            || 'Sans référence' }} — Slot {{ badge.slot != null ? badge.slot : '—' }}</option>
                    </select>
                    <button type="button" class="add-button" :disabled="busy || !selectedBadgeId" @click="addBadge">{{
                        working ? 'Patientez…' : 'Associer' }}</button>
                </div>
                <p class="badge-hint">Seuls les badges sans chauffeur sont proposés.</p>
            </div>
        </template>
        <p v-if="actionError" class="badge-error" role="alert">{{ actionError }}</p>
        <p v-if="success" class="badge-success" role="status">{{ success }}</p>
    </section>
</template>

<script>
import DriverRepository from '@/repositories/driver.repo.js';
import BadgeRepository from '@/repositories/badge.repo.js';

export default {
    name: 'DriverBadges',
    emits: ['changed'],
    props: {
        driverId: { type: [Number, String], required: true },
        disabled: { type: Boolean, default: false }
    },
    data() {
        return {
            badges: [], allBadges: [], selectedBadgeId: '',
            loading: true, loadingAvailable: true, working: false,
            loadError: '', actionError: '', success: ''
        };
    },
    computed: {
        busy() { return this.disabled || this.working || this.loading || this.loadingAvailable; },
        availableBadges() {
            return this.allBadges.filter(
                badge =>
                    badge.status === 'ACTIVE' &&
                    badge.driverId == null &&
                    !this.badges.some(assigned => assigned.id === badge.id)
            );
        }
    },
    mounted() { this.refresh(); },
    methods: {
        async refresh() {
            this.loading = true;
            this.loadingAvailable = true;
            this.loadError = '';
            this.actionError = '';
            try {
                const [badges, allBadges] = await Promise.all([
                    DriverRepository.getDriverBadges(this.driverId),
                    BadgeRepository.getBadges()
                ]);
                this.badges = badges;
                this.allBadges = allBadges;
                this.selectedBadgeId = '';
            } catch (err) {
                this.loadError = 'Impossible de charger les badges.';
                console.error(err);
            } finally {
                this.loading = false;
                this.loadingAvailable = false;
            }
        },
        async loadBadges() { await this.refresh(); },
        async addBadge() {
            if (this.busy || !this.selectedBadgeId) return;
            this.working = true;
            this.actionError = '';
            this.success = '';
            try {
                await DriverRepository.addDriverBadge(this.driverId, this.selectedBadgeId);
                this.$emit('changed');
                await this.refresh();
                if (!this.loadError) this.success = 'Badge associé.';
            } catch (err) {
                this.actionError = err.response?.status === 409
                    ? 'Ce badge est déjà attribué. Actualisez les badges et réessayez.'
                    : 'Impossible d’associer ce badge.';
                console.error(err);
            } finally { this.working = false; }
        },
        async removeBadge(badge) {
            if (this.busy) return;
            this.working = true;
            this.actionError = '';
            this.success = '';
            try {
                await DriverRepository.removeDriverBadge(this.driverId, badge.id);
                this.$emit('changed');
                await this.refresh();
                if (!this.loadError) this.success = 'Badge dissocié.';
            } catch (err) {
                this.actionError = 'Impossible de dissocier ce badge.';
                console.error(err);
            } finally { this.working = false; }
        }
    }
};
</script>

<style scoped>
.badges-panel {
    border-top: 1px solid #edf0f5;
    margin-top: 26px;
    padding-top: 24px;
}

.badges-heading {
    display: flex;
    justify-content: space-between;
    align-items: start;
    gap: 12px;
    margin-bottom: 18px;
}

.badges-heading h3 {
    margin: 0 0 5px;
    color: #1c2c45;
    font-size: 16px;
    font-weight: 750;
}

.badges-heading p,
.badge-hint {
    color: #77849a;
    font-size: 12px;
    line-height: 1.5;
    margin: 0;
}

.badge-count {
    background: #eef4fc;
    color: #24538d;
    border-radius: 999px;
    padding: 3px 10px;
    font-size: 12px;
    font-weight: 750;
}

.badge-empty {
    color: #78869a;
    font-size: 13px;
    padding: 18px 14px;
    background: #f7f9fc;
    border-radius: 10px;
}

.badge-list {
    margin: 0 0 22px;
    padding: 0;
    list-style: none;
}

.badge-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    padding: 13px 0;
    border-bottom: 1px solid #edf0f5;
}

.badge-info {
    display: flex;
    flex-direction: column;
    min-width: 0;
    gap: 3px;
    overflow-wrap: anywhere;
}

.badge-info strong {
    color: #25354d;
    font-size: 13px;
}

.badge-info span {
    color: #697b92;
    font-size: 12px;
}

.remove-button {
    border: 1px solid #e5c9c9;
    background: #fff;
    color: #a03e3e;
    border-radius: 8px;
    font-size: 12px;
    font-weight: 700;
    padding: 7px 10px;
    cursor: pointer;
}

.badge-association {
    padding: 15px;
    background: #f7f9fc;
    border: 1px solid #e8edf4;
    border-radius: 10px;
}

.badge-association label {
    display: block;
    font-size: 13px;
    font-weight: 700;
    color: #33435b;
    margin-bottom: 9px;
}

.association-controls {
    display: flex;
    gap: 8px;
}

.association-controls select {
    flex: 1;
    min-width: 0;
    width: 100%;
    padding: 9px;
    border: 1px solid #d8e1ed;
    border-radius: 8px;
    background: white;
    color: #25354d;
    font-size: 12px;
}

.add-button {
    background: #1b4f95;
    color: white;
    border: 1px solid #1b4f95;
    border-radius: 8px;
    font-size: 12px;
    font-weight: 750;
    padding: 9px 12px;
    cursor: pointer;
}

.badge-association .badge-hint {
    margin-top: 9px;
}

.badge-error,
.badge-success {
    margin: 12px 0 0;
    padding: 10px;
    font-size: 12px;
    border-radius: 8px;
}

.badge-error {
    background: #fff0f0;
    color: #a32929;
}

.badge-error button {
    background: none;
    border: 0;
    color: inherit;
    font-weight: 700;
    text-decoration: underline;
    cursor: pointer;
}

.badge-success {
    background: #eaf7ef;
    color: #136243;
}

button:disabled {
    opacity: .55;
    cursor: not-allowed;
}

@media (max-width: 520px) {
    .association-controls {
        flex-direction: column;
    }
}
</style>
